import React, { useState, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Laptop } from '@/types/models';
import { reconfigureInStockLaptopRpc } from '@/services/laptops';
import { fetchAllActiveAccessories } from '@/services/accessories';
import { detectComponentType, resolveSaleHardware } from '@/lib/saleHardware';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { queryKeys } from '@/lib/queryKeys';
import { parseDatabaseError } from '@/lib/errors';
import {
  Wrench,
  X,
  Plus,
  Minus,
  Loader2,
  AlertTriangle,
} from 'lucide-react';

interface ReconfigureHardwareModalProps {
  laptop: Laptop;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface InstalledRow {
  component_type: 'ram' | 'storage';
  accessory_id: string;
  quantity: number;
}

interface RecoveredRow {
  component_type: 'ram' | 'storage';
  mode: 'existing' | 'new';
  accessory_id: string;
  name: string;
  category: string;
  quantity: number;
  unit_cost: number;
}

export const ReconfigureHardwareModal: React.FC<ReconfigureHardwareModalProps> = ({
  laptop,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const queryClient = useQueryClient();

  const currentCostBasis = Number(laptop.current_cost_basis ?? laptop.landed_cost ?? laptop.purchase_price ?? 0);

  const [reason, setReason] = useState('');
  const [newRam, setNewRam] = useState(laptop.ram ?? '');
  const [newStorage, setNewStorage] = useState(laptop.storage ?? '');
  const [clearRam, setClearRam] = useState(false);
  const [clearStorage, setClearStorage] = useState(false);
  const [installedRows, setInstalledRows] = useState<InstalledRow[]>([]);
  const [recoveredRows, setRecoveredRows] = useState<RecoveredRow[]>([]);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const { data: accessories = [], error: accessoriesError, isLoading: loadingAccessories, refetch: retryAccessories } = useQuery({
    queryKey: ['accessories', 'reconfig-all'],
    queryFn: fetchAllActiveAccessories,
    enabled: isOpen,
  });
  const ramAndStorageAccessories = useMemo(() => accessories.filter(a => detectComponentType(a) !== null), [accessories]);

  const addInstalledRow = () => {
    setInstalledRows((p) => [
      ...p,
      { component_type: 'ram', accessory_id: '', quantity: 1 },
    ]);
  };

  const updateInstalledRow = (idx: number, field: keyof InstalledRow, val: any) => {
    setInstalledRows((p) =>
      p.map((r, i) => {
        if (i !== idx) return r;
        const part = field === 'accessory_id' ? accessories.find(a => a.id === val) : undefined;
        return { ...r, [field]: val, component_type: part ? detectComponentType(part) || r.component_type : field === 'component_type' ? val : r.component_type };
      })
    );
  };

  const removeInstalledRow = (idx: number) => {
    setInstalledRows((p) => p.filter((_, i) => i !== idx));
  };

  const addRecoveredRow = () => {
    setRecoveredRows((p) => [
      ...p,
      {
        component_type: 'ram',
        mode: 'existing',
        accessory_id: '',
        name: '',
        category: 'RAM',
        quantity: 1,
        unit_cost: 0,
      },
    ]);
  };

  const updateRecoveredRow = (idx: number, field: keyof RecoveredRow, val: any) => {
    setRecoveredRows((p) =>
      p.map((r, i) => {
        if (i !== idx) return r;
        const updated = { ...r, [field]: val };
        if (field === 'component_type') {
          updated.category = val === 'ram' ? 'RAM' : 'Storage';
        }
        if (field === 'accessory_id' && val) {
          const matchedAcc = accessories.find((a) => a.id === val);
          if (matchedAcc) {
            updated.component_type = detectComponentType(matchedAcc) || r.component_type;
            updated.category = updated.component_type === 'ram' ? 'RAM' : 'Storage';
            updated.unit_cost = matchedAcc.average_cost || 0;
          }
        }
        if (field === 'name' && r.mode === 'new') {
          const detected = detectComponentType({ name: val });
          if (detected) { updated.component_type = detected; updated.category = detected === 'ram' ? 'RAM' : 'Storage'; }
        }
        return updated;
      })
    );
  };

  const removeRecoveredRow = (idx: number) => {
    setRecoveredRows((p) => p.filter((_, i) => i !== idx));
  };

  // Financial calculations
  const totalInstalledCost = useMemo(() => {
    return installedRows.reduce((sum, row) => {
      if (!row.accessory_id) return sum;
      const acc = accessories.find((a) => a.id === row.accessory_id);
      return sum + (row.quantity || 0) * (acc?.average_cost || 0);
    }, 0);
  }, [installedRows, accessories]);

  const totalRecoveredCost = useMemo(() => {
    return recoveredRows.reduce((sum, row) => {
      return sum + (row.quantity || 0) * (row.unit_cost || 0);
    }, 0);
  }, [recoveredRows]);

  // Unclamped projected cost basis
  const projectedCostBasis = currentCostBasis + totalInstalledCost - totalRecoveredCost;

  const { mutate, isPending } = useMutation({
    mutationFn: async () => {
      if (loadingAccessories || accessoriesError) throw new Error('Load component stock successfully before reconfiguring hardware.');
      if (!reason.trim()) throw new Error('A reason is required to reconfigure hardware.');

      if (projectedCostBasis < 0) {
        throw new Error('Projected cost basis cannot be negative. Recovered parts credit exceeds current cost basis plus installed parts.');
      }

      // Validate installed components
      for (const row of installedRows) {
        if (!row.accessory_id) {
          throw new Error('Please select an accessory for all installed components.');
        }
        const acc = accessories.find((a) => a.id === row.accessory_id);
        if (acc && acc.quantity < row.quantity) {
          throw new Error(`Insufficient stock for accessory "${acc.name}". Available: ${acc.quantity}`);
        }
      }

      // Validate recovered components
      for (const row of recoveredRows) {
        if (row.mode === 'existing' && !row.accessory_id) {
          throw new Error('Please select a target accessory or switch to creating a new accessory for recovered items.');
        }
        if (row.mode === 'new' && !row.name.trim()) {
          throw new Error('Please enter a name for the new recovered accessory.');
        }
        if (row.unit_cost < 0) {
          throw new Error('Recovered unit cost cannot be negative.');
        }
      }

      for (const row of [...installedRows, ...recoveredRows]) {
        const part = accessories.find(a => a.id === row.accessory_id);
        const detected = part ? detectComponentType(part) : null;
        if (detected && detected !== row.component_type) throw new Error(`${part!.name}: choose the matching ${detected === 'ram' ? 'RAM' : 'Storage'} component type.`);
      }
      const hardware = resolveSaleHardware({
        label: laptop.stock_id, originalRam: laptop.ram, originalStorage: laptop.storage,
        finalRam: newRam, finalStorage: newStorage, clearRam, clearStorage,
        actions: [
          ...installedRows.map(r => ({ ...r, direction: 'installed' as const, unit_cost: accessories.find(a => a.id === r.accessory_id)?.average_cost ?? 0 })),
          ...recoveredRows.map((r, i) => ({ ...r, accessory_id: r.mode === 'new' ? `new-${i}` : r.accessory_id, direction: 'recovered' as const })),
        ],
      });
      return reconfigureInStockLaptopRpc({
        laptop_id: laptop.id,
        reason: reason.trim(),
        new_ram: hardware.ram,
        new_storage: hardware.storage,
        clear_ram: hardware.clear_ram,
        clear_storage: hardware.clear_storage,
        installed_components: installedRows.map((r) => ({
          component_type: r.component_type,
          accessory_id: r.accessory_id,
          quantity: r.quantity,
        })),
        recovered_components: recoveredRows.map((r) => ({
          component_type: r.component_type,
          accessory_id: r.mode === 'existing' ? r.accessory_id || null : null,
          name: r.mode === 'new' ? r.name.trim() : null,
          category: r.mode === 'new' ? (r.category.trim() || (r.component_type === 'ram' ? 'RAM' : 'Storage')) : null,
          quantity: r.quantity,
          unit_cost: r.unit_cost,
        })),
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.laptops.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.laptops.detail(laptop.id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.accessories.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.reports.all });
      onSuccess?.();
      onClose();
    },
    onError: (err: unknown) => {
      setErrorMsg(parseDatabaseError(err));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    mutate();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-brand-border">
          <div className="flex items-center space-x-2">
            <Wrench className="w-5 h-5 text-purple-600" />
            <div>
              <h2 className="font-bold text-brand-black text-sm">
                Reconfigure In-Stock Hardware — {laptop.stock_id}
              </h2>
              <p className="text-[11px] text-brand-muted">
                {laptop.brand} {laptop.model} {laptop.cpu ? `• ${laptop.cpu}` : ''}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-brand-muted hover:text-brand-black transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="overflow-y-auto flex-1 px-5 py-4 space-y-5 text-xs">
            {/* Warning Banner */}
            <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg text-purple-900 space-y-1">
              <div className="flex items-center space-x-2">
                <AlertTriangle className="w-4 h-4 text-purple-600 shrink-0" />
                <p className="font-bold">Owner-Only Hardware Reconfiguration</p>
              </div>
              <p className="text-[11px] text-purple-800">
                Swapping RAM or Storage modifies this laptop's cost basis and automatically transfers components into or out of shop accessory inventory with weighted average cost accounting.
              </p>
            </div>

            {/* Current Specs & Cost Box */}
            <div className="grid grid-cols-3 gap-3 bg-slate-50 p-3 rounded-lg border border-slate-200">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-brand-muted">Current RAM</p>
                <p className="font-bold text-brand-black text-xs">{laptop.ram || 'None / Not set'}</p>
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-brand-muted">Current Storage</p>
                <p className="font-bold text-brand-black text-xs">{laptop.storage || 'None / Not set'}</p>
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-brand-muted">Current Cost Basis</p>
                <p className="font-bold text-purple-800 text-xs">Rs. {currentCostBasis.toLocaleString('en-PK')}</p>
              </div>
            </div>

            {/* Reason (Required) */}
            <div>
              <label className="block font-bold text-brand-black mb-1">
                Reconfiguration Reason <span className="text-brand-red">*</span>
              </label>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Upgraded to 16GB RAM and 512GB NVMe SSD for showroom floor display"
                className="w-full border border-brand-border rounded-md px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-purple-500"
                required
              />
            </div>

            {/* ── 1. Recovered Components (Parts Removed from Laptop -> Added to Shop Stock) ── */}
            <div className="border border-green-200 rounded-lg p-3 bg-green-50/40 space-y-2.5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-bold text-green-950 uppercase tracking-wider text-[10px] flex items-center space-x-1.5">
                    <span>1. Recovered Components (Return to Shop Stock)</span>
                  </p>
                  <p className="text-[10px] text-green-800">
                    Old parts removed from this laptop will be credited to accessory inventory and deducted from the laptop's cost basis.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={addRecoveredRow}
                  className="inline-flex items-center space-x-1 px-2.5 py-1 text-xs bg-green-600 text-white font-semibold rounded hover:bg-green-700 transition-colors shadow-2xs"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>Add Recovered Part</span>
                </button>
              </div>

              {recoveredRows.length === 0 ? (
                <p className="text-[11px] text-green-800/80 italic py-1">No parts removed / recovered.</p>
              ) : (
                <div className="space-y-2 pt-1">
                  {recoveredRows.map((row, idx) => (
                    <div key={idx} className="p-2.5 bg-white border border-green-200 rounded-md shadow-2xs space-y-2">
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 items-center">
                        <div>
                          <label className="block text-[10px] font-bold text-brand-muted mb-0.5">Component Type</label>
                          <select
                            value={row.component_type}
                            onChange={(e) => updateRecoveredRow(idx, 'component_type', e.target.value)}
                            className="w-full border border-brand-border rounded px-2 py-1 text-xs bg-white"
                          >
                            <option value="ram">RAM</option>
                            <option value="storage">Storage / SSD</option>
                          </select>
                        </div>
                        <div className="sm:col-span-2">
                          <label className="block text-[10px] font-bold text-brand-muted mb-0.5">
                            Target Accessory in Stock
                          </label>
                          {row.mode === 'existing' ? (
                            <select
                              value={row.accessory_id}
                              onChange={(e) => updateRecoveredRow(idx, 'accessory_id', e.target.value)}
                              className="w-full border border-brand-border rounded px-2 py-1 text-xs bg-white"
                            >
                              <option value="">— Select existing accessory —</option>
                              {(ramAndStorageAccessories.length > 0 ? ramAndStorageAccessories : accessories).map((a) => (
                                <option key={a.id} value={a.id}>
                                  {a.name} (Stock: {a.quantity}, Avg: Rs.{a.average_cost})
                                </option>
                              ))}
                            </select>
                          ) : (
                            <input
                              type="text"
                              value={row.name}
                              onChange={(e) => updateRecoveredRow(idx, 'name', e.target.value)}
                              placeholder="e.g. 8GB DDR4 3200MHz Laptop RAM"
                              className="w-full border border-brand-border rounded px-2 py-1 text-xs bg-white"
                            />
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="w-16">
                            <label className="block text-[10px] font-bold text-brand-muted mb-0.5">Qty</label>
                            <input
                              type="number"
                              min={1}
                              value={row.quantity}
                              onChange={(e) => updateRecoveredRow(idx, 'quantity', parseInt(e.target.value) || 1)}
                              className="w-full border border-brand-border rounded px-2 py-1 text-xs text-center"
                            />
                          </div>
                          <div className="flex-1">
                            <label className="block text-[10px] font-bold text-brand-muted mb-0.5">Unit Value (Rs.)</label>
                            <input
                              type="number"
                              min={0}
                              step={1}
                              value={row.unit_cost}
                              onChange={(e) => updateRecoveredRow(idx, 'unit_cost', parseFloat(e.target.value) || 0)}
                              className="w-full border border-brand-border rounded px-2 py-1 text-xs text-right"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => removeRecoveredRow(idx)}
                            className="p-1 text-red-600 hover:text-red-800 mt-4"
                            title="Remove row"
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      <div className="flex items-center justify-between text-[10px] text-brand-muted pt-1 border-t border-slate-100">
                        <label className="flex items-center space-x-1.5 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={row.mode === 'new'}
                            onChange={(e) => updateRecoveredRow(idx, 'mode', e.target.checked ? 'new' : 'existing')}
                            className="accent-green-600"
                          />
                          <span>Create as new accessory SKU instead of adding to existing</span>
                        </label>
                        <span className="font-semibold text-green-800">
                          Total Credited: Rs. {((row.quantity || 0) * (row.unit_cost || 0)).toLocaleString('en-PK')}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ── 2. Installed Components (Parts Deducted from Shop Stock -> Added to Laptop) ── */}
            <div className="border border-purple-200 rounded-lg p-3 bg-purple-50/40 space-y-2.5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-bold text-purple-950 uppercase tracking-wider text-[10px] flex items-center space-x-1.5">
                    <span>2. Installed Components (Deduct from Shop Stock)</span>
                  </p>
                  <p className="text-[10px] text-purple-800">
                    New parts taken from accessory stock will be added to this laptop's cost basis and stock deducted.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={addInstalledRow}
                  className="inline-flex items-center space-x-1 px-2.5 py-1 text-xs bg-purple-600 text-white font-semibold rounded hover:bg-purple-700 transition-colors shadow-2xs"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>Add Installed Part</span>
                </button>
              </div>

              {installedRows.length === 0 ? (
                <p className="text-[11px] text-purple-800/80 italic py-1">No new parts installed.</p>
              ) : (
                <div className="space-y-2 pt-1">
                  {installedRows.map((row, idx) => {
                    const acc = accessories.find((a) => a.id === row.accessory_id);
                    return (
                      <div key={idx} className="p-2.5 bg-white border border-purple-200 rounded-md shadow-2xs">
                        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 items-center">
                          <div>
                            <label className="block text-[10px] font-bold text-brand-muted mb-0.5">Component Type</label>
                            <select
                              value={row.component_type}
                              onChange={(e) => updateInstalledRow(idx, 'component_type', e.target.value)}
                              className="w-full border border-brand-border rounded px-2 py-1 text-xs bg-white"
                            >
                              <option value="ram">RAM</option>
                              <option value="storage">Storage / SSD</option>
                            </select>
                          </div>
                          <div className="sm:col-span-2">
                            <label className="block text-[10px] font-bold text-brand-muted mb-0.5">
                              Select from Accessory Inventory
                            </label>
                            <select
                              value={row.accessory_id}
                              onChange={(e) => updateInstalledRow(idx, 'accessory_id', e.target.value)}
                              className="w-full border border-brand-border rounded px-2 py-1 text-xs bg-white"
                            >
                              <option value="">— Select accessory in stock —</option>
                              {(ramAndStorageAccessories.length > 0 ? ramAndStorageAccessories : accessories).map((a) => (
                                <option key={a.id} value={a.id}>
                                  {a.name} (Stock: {a.quantity}, Cost: Rs.{a.average_cost})
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div className="w-16">
                              <label className="block text-[10px] font-bold text-brand-muted mb-0.5">Qty</label>
                              <input
                                type="number"
                                min={1}
                                max={acc ? acc.quantity : 999}
                                value={row.quantity}
                                onChange={(e) => updateInstalledRow(idx, 'quantity', parseInt(e.target.value) || 1)}
                                className="w-full border border-brand-border rounded px-2 py-1 text-xs text-center"
                              />
                            </div>
                            <div className="flex-1 text-right">
                              <label className="block text-[10px] font-bold text-brand-muted mb-0.5">Line Total</label>
                              <p className="font-bold text-purple-900 text-xs py-1">
                                Rs. {((row.quantity || 0) * (acc?.average_cost || 0)).toLocaleString('en-PK')}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeInstalledRow(idx)}
                              className="p-1 text-red-600 hover:text-red-800 mt-4"
                              title="Remove row"
                            >
                              <Minus className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* ── 3. Final Specification Strings ── */}
            <div className="border border-brand-border rounded-lg p-3 bg-slate-50/60 space-y-3">
              <p className="font-bold text-brand-black uppercase tracking-wider text-[10px]">
                3. Final Hardware Specs (Display & Invoicing)
              </p>
              <p className="text-[11px] text-brand-muted">Select None to remove RAM or storage without installing a replacement. Record only the parts actually recovered to shop stock.</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="font-bold text-brand-black">Final RAM</label>
                    <button
                      type="button"
                      onClick={() => {
                        setNewRam('');
                        setClearRam(true);
                      }}
                      className={`text-[10px] underline ${clearRam ? 'text-purple-600 font-bold' : 'text-brand-muted hover:text-brand-black'}`}
                    >
                      {clearRam ? '✓ None selected' : 'Select None'}
                    </button>
                  </div>
                  <input
                    type="text"
                    value={clearRam ? 'None' : newRam}
                    disabled={clearRam}
                    onChange={(e) => {
                      setNewRam(e.target.value);
                      setClearRam(false);
                    }}
                    placeholder={clearRam ? '(Cleared / None)' : 'e.g. 16GB DDR4'}
                    className={`w-full border border-brand-border rounded px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-purple-500 ${clearRam ? 'bg-slate-100 text-slate-400 italic' : 'bg-white'}`}
                  />
                  {clearRam && (
                    <button
                      type="button"
                      onClick={() => { setClearRam(false); setNewRam(laptop.ram ?? ''); }}
                      className="text-[9px] text-blue-600 hover:underline mt-0.5"
                    >
                      Undo clear
                    </button>
                  )}
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="font-bold text-brand-black">Final Storage</label>
                    <button
                      type="button"
                      onClick={() => {
                        setNewStorage('');
                        setClearStorage(true);
                      }}
                      className={`text-[10px] underline ${clearStorage ? 'text-purple-600 font-bold' : 'text-brand-muted hover:text-brand-black'}`}
                    >
                      {clearStorage ? '✓ None selected' : 'Select None'}
                    </button>
                  </div>
                  <input
                    type="text"
                    value={clearStorage ? 'None' : newStorage}
                    disabled={clearStorage}
                    onChange={(e) => {
                      setNewStorage(e.target.value);
                      setClearStorage(false);
                    }}
                    placeholder={clearStorage ? '(Cleared / None)' : 'e.g. 512GB NVMe SSD'}
                    className={`w-full border border-brand-border rounded px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-purple-500 ${clearStorage ? 'bg-slate-100 text-slate-400 italic' : 'bg-white'}`}
                  />
                  {clearStorage && (
                    <button
                      type="button"
                      onClick={() => { setClearStorage(false); setNewStorage(laptop.storage ?? ''); }}
                      className="text-[9px] text-blue-600 hover:underline mt-0.5"
                    >
                      Undo clear
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* ── 4. Financial Summary Breakdown ── */}
            <div className="p-3.5 bg-slate-900 text-white rounded-lg space-y-2 text-xs">
              <p className="font-bold uppercase tracking-wider text-[10px] text-slate-300">
                Financial Impact Preview
              </p>
              <div className="grid grid-cols-4 gap-2 text-center pt-1">
                <div className="bg-slate-800 p-2 rounded">
                  <p className="text-[10px] text-slate-400">Old Cost Basis</p>
                  <p className="font-bold text-white text-xs mt-0.5">
                    Rs. {currentCostBasis.toLocaleString('en-PK')}
                  </p>
                </div>
                <div className="bg-slate-800 p-2 rounded">
                  <p className="text-[10px] text-purple-300">+ Installed Parts</p>
                  <p className="font-bold text-purple-300 text-xs mt-0.5">
                    + Rs. {totalInstalledCost.toLocaleString('en-PK')}
                  </p>
                </div>
                <div className="bg-slate-800 p-2 rounded">
                  <p className="text-[10px] text-green-300">- Recovered Parts</p>
                  <p className="font-bold text-green-300 text-xs mt-0.5">
                    - Rs. {totalRecoveredCost.toLocaleString('en-PK')}
                  </p>
                </div>
                <div className={`p-2 rounded border ${projectedCostBasis < 0 ? 'bg-red-950/80 border-red-500 text-red-300' : 'bg-purple-950/80 border-purple-500/50'}`}>
                  <p className="text-[10px] text-purple-200 font-bold">= New Cost Basis</p>
                  <p className={`font-bold text-xs mt-0.5 ${projectedCostBasis < 0 ? 'text-red-400 font-extrabold' : 'text-green-400'}`}>
                    Rs. {projectedCostBasis.toLocaleString('en-PK')}
                  </p>
                </div>
              </div>
              {projectedCostBasis < 0 && (
                <div className="p-2 bg-red-900/80 border border-red-500 rounded text-red-200 text-[11px] font-bold">
                  ⚠️ Error: Resulting cost basis cannot be negative. Total recovered value exceeds old cost plus installed components.
                </div>
              )}
            </div>

            {accessoriesError && <ErrorAlert message={parseDatabaseError(accessoriesError)} onRetry={() => { void retryAccessories(); }} />}
          {errorMsg && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 font-semibold">
                {errorMsg}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-5 py-4 border-t border-brand-border bg-brand-bg">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-brand-muted hover:text-brand-black transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending || !reason.trim() || projectedCostBasis < 0}
              className="inline-flex items-center space-x-2 px-5 py-2 bg-purple-600 text-white text-sm font-bold rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              {isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              <span>{isPending ? 'Reconfiguring Hardware…' : 'Save & Reconfigure Hardware'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
